import { ReactNode } from 'react';
import { Link, Navigate, Route, Routes, useParams } from 'react-router-dom';

import { Box, HStack, Tabs, TabsRootProps as ChakraTabsRootProps } from '@oaknorth/component-core-v2';
import { T } from '@oaknorth/util-intl';

interface RouterTabDetail {
  id: string;
  label: ReactNode;
  content: ReactNode;
  /**
   * Query parameters to be applied to the tab URL.
   * The search parameter won't be used to indentify if a tab is active (just the `id` property)
   */
  search?: string;
}

export interface RouterTabsProps extends TabsRootProps {
  /**
   * Definitions of the tabs to show
   */
  tabs: RouterTabDetail[];
  /**
   * ID of the tab to redirect to if no tab is initially selected
   * @default id from the first tab in `tabs`
   */
  defaultTabId?: string;
}

// Omit props which conflict with the routing handling
// Make id required - used for IDs for accessibility, tracking
type TabsRootProps = Omit<ChakraTabsRootProps, 'defaultIndex' | 'index' | 'isLazy' | 'children'> & {
  id: string;
  getTabsRightElement?: (tabId: string) => ReactNode;
};

interface RouterTabsInnerProps {
  tabs: RouterTabDetail[];
  tabsProps: TabsRootProps;
}

// Separate inner component to allow us to use `useParams`
function RouterTabsInner({ tabs, tabsProps }: RouterTabsInnerProps) {
  const { tabId } = useParams<{ tabId: string }>();
  const activeTabId = tabs.find((tab) => tab.id === tabId)?.id;
  const { getTabsRightElement, ...restTabsProps } = tabsProps;

  return (
    <Tabs.Root {...restTabsProps} lazyMount unmountOnExit value={activeTabId}>
      <HStack justifyContent="space-between">
        <Tabs.List>
          {tabs.map((tab) => (
            <Tabs.Trigger key={tab.id} value={tab.id} asChild data-tabid={tab.id}>
              <Link
                to={{
                  pathname: `./../${tab.id}`,
                  search: tab.search,
                }}
              >
                {tab.label}
              </Link>
            </Tabs.Trigger>
          ))}
        </Tabs.List>
        {getTabsRightElement && tabId ? getTabsRightElement(tabId) : null}
      </HStack>

      {tabs.map((tab) => (
        <Tabs.Content key={tab.id} value={tab.id}>
          {tab.content}
        </Tabs.Content>
      ))}

      {/* Tab not found */}
      {activeTabId === undefined && (
        <Box width="100%" padding={4}>
          {T`routerTabs.notFound`}
        </Box>
      )}
    </Tabs.Root>
  );
}

/**
 * `Tabs` which are linked to the URL - each tab is a separate subpath, using the `id`
 */
export function RouterTabs({ tabs, defaultTabId = tabs[0].id, ...props }: RouterTabsProps) {
  const defaultTab = tabs.find((tab) => tab.id === defaultTabId);

  return (
    <Routes>
      <Route path=":tabId/*" element={<RouterTabsInner tabs={tabs} tabsProps={props} />} />
      <Route index element={<Navigate to={{ pathname: defaultTabId, search: defaultTab?.search }} replace />} />
    </Routes>
  );
}
